import { zodResolver } from "@hookform/resolvers/zod";
import { Stack } from "@mui/system";
import { FunctionComponent, useCallback, useEffect, useState } from "react"; // Added React import
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
	useCreateCoursesMutation,
	useGetTagsQuery,
} from "@/pages/Private/redux/courses/courses.api";
import { InputField } from "@/components/InputField/InputField";
import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";
import { ImageUploader } from "@/components/ImageUploader/ImageUploader";
import { VideoUploader } from "@/components/VideoUploader/VideoUploader";
import { usePageTitle } from "@/utils/usePageTitle";
import { AutoComplete, AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";

import { languages } from "@/constants";

import { UserLanguage } from "@/enum/user.enum";

import { DnDProvider } from "./Editor/DnDContext";

import { EMPTY_COURSE_SCENARIO, IStageContentInfo } from "../constants/emptyCourseScenario";
import { CourseTag, CreateCourses, CreateCoursesSchema } from "../schema/courses";
import { CourseStatus } from "../enums/CourseStatus.enum";
import { IFileInfo } from "./FilesUploader";
import ContentModal from "../../Course/components/ContentModal";
import { FileAssignment } from "../enums/FileAssignment.enum";

import LanguageSelect from "../../Account/components/LanguageSelect";
import { TEMPLATES } from "../constants/templates";

export const CreateCoursesForm: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`courses.${key}`), [t]);
	const { data: tags } = useGetTagsQuery();

	usePageTitle(ts("create.title"));
	const navigate = useNavigate();
	const [createCourses, { isLoading }] = useCreateCoursesMutation();

	const {
		handleSubmit,
		setValue,
		formState: { errors },
	} = useForm<CreateCourses>({
		defaultValues: {
			name: undefined,
			json: EMPTY_COURSE_SCENARIO,
			description: undefined,
			tag: undefined,
			language: UserLanguage.DE,
			forWhom: undefined,
			// The schema requires a status, and a new scenario starts unpublished — without
			// this default the resolver silently blocked every submit.
			status: CourseStatus.Draft,
		},
		resolver: zodResolver(CreateCoursesSchema),
	});

	const [currentFormState, setCurrentFormState] = useState<Partial<CreateCourses>>({
		name: undefined,
		json: EMPTY_COURSE_SCENARIO, // Ensure this is a valid initial JSON structure
		tag: undefined,
		description: undefined,
		language: UserLanguage.DE,
		forWhom: undefined,
		status: CourseStatus.Draft,
	});
	const [files, setFiles] = useState<IFileInfo[]>([]);
	const [thumbnailImageFileInfo, setThumbnailImageFileInfo] = useState<IFileInfo | null>(null);
	const [trailerVideoFileInfo, setTrailerFileInfo] = useState<IFileInfo | null>(null);
	const [largeImageFileInfo, setLargeImageFileInfo] = useState<IFileInfo | null>(null);
	const [currentShowingContentInfo, setCurrentShowingContentInfo] =
		useState<IStageContentInfo | null>(null);

	const DRAFT_KEY = "create-course-draft";

	useEffect(() => {
		const savedDraftJson = localStorage.getItem(DRAFT_KEY);

		if (savedDraftJson) {
			try {
				const draft = JSON.parse(savedDraftJson);

				// Lokalen State und react-hook-form State aktualisieren
				if (draft.formState) {
					setCurrentFormState(draft.formState);
					Object.keys(draft.formState).forEach((key) => {
						setValue(key as keyof CreateCourses, draft.formState[key]);
					});
				}

				// File-States wiederherstellen
				setFiles(draft.files || []);
				setThumbnailImageFileInfo(draft.thumbnail || null);
				setTrailerFileInfo(draft.trailer || null);
				setLargeImageFileInfo(draft.largeImage || null);
			} catch (e) {
				console.error("Fehler beim Laden des Szenario-Entwurfs:", e);
				localStorage.removeItem(DRAFT_KEY); // Fehlerhaften Entwurf löschen
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [setValue]); // Läuft nur einmal beim Mounten

	useEffect(() => {
		const draft = {
			formState: currentFormState,
			files,
			thumbnail: thumbnailImageFileInfo,
			trailer: trailerVideoFileInfo,
			largeImage: largeImageFileInfo,
		};

		// Prüfen, ob der Graph verändert wurde (mehr als die Start-Node enthält)
		const isGraphModified = (currentFormState.json?.Content?.length ?? 0) > 1;

		// Prüfen, ob irgendein anderes Feld oder eine Datei hinzugefügt wurde
		const isFormStarted =
			currentFormState.name ||
			currentFormState.language ||
			currentFormState.description ||
			currentFormState.forWhom ||
			files.length > 0 ||
			thumbnailImageFileInfo ||
			trailerVideoFileInfo ||
			largeImageFileInfo;

		// Speichere den Entwurf, wenn der Graph oder das Formular bearbeitet wurde.
		if (isGraphModified || isFormStarted) {
			localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
		}
	}, [currentFormState, files, largeImageFileInfo, thumbnailImageFileInfo, trailerVideoFileInfo]);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleSaveFormState = (value: any, name: keyof CreateCourses) => {
		setCurrentFormState((prevState) => ({
			...prevState,
			[name]: value,
		}));

		setValue(name, value, {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	const onSubmit = async (values: CreateCourses) => {
		try {
			const courseFiles = [
				...files.map((fileInfo) => ({
					// General course files
					id: fileInfo.id,
					fullFilePath: fileInfo.fullFilePath,
					fileName: fileInfo.fileName,
					fileLength: fileInfo.fileLength,
					fileType: fileInfo.fileType,
					fileAssignment: fileInfo.fileAssignment || FileAssignment.Content, // Ensure assignment
					hash: fileInfo.hash,
				})),
				...(thumbnailImageFileInfo
					? [{ ...thumbnailImageFileInfo, fileAssignment: FileAssignment.Thumbnail }]
					: []),
				...(trailerVideoFileInfo
					? [{ ...trailerVideoFileInfo, fileAssignment: FileAssignment.TrailerVideo }]
					: []),
				...(largeImageFileInfo
					? [{ ...largeImageFileInfo, fileAssignment: FileAssignment.LargeImage }]
					: []),
			];

			const result = await createCourses({
				...values, // Contains name, description, forWhom, tag from form
				json:
					typeof currentFormState.json === "string"
						? JSON.parse(currentFormState.json || "{}") // Should be object already
						: currentFormState.json || EMPTY_COURSE_SCENARIO,
				courseFiles: courseFiles,
			}).unwrap();

			if (result?.id) {
				localStorage.removeItem(DRAFT_KEY);
				navigate(`/app/scenarios/edit/${result.id}`, { replace: true });
			}
		} catch (err) {
			console.error("Error creating course:", err);
		}
	};
	const handleClose = () => {
		localStorage.removeItem(DRAFT_KEY);
		navigate(-1);
	};

	const handleSetTemplate = (value?: AutoCompleteItem) => {
		if (value && value?.title) {
			handleSaveFormState(TEMPLATES?.find((template) => template?.id === value?.id)?.json, "json");
		} else {
			handleSaveFormState(null, "json");
		}
	};

	const handleSetTags = (value?: AutoCompleteItem) => {
		if (value && value?.title) {
			handleSaveFormState(
				{
					id: value?.id ?? -1, // Ensure ID is a number
					name: value.title,
				} as CourseTag,
				"tag"
			);
		}
	};

	return (
		<DnDProvider>
			<>
				<div className="flex flex-col h-[calc(100vh-97px)]">
					<div className="flex flex-col md:flex-row w-full h-[calc(100%-80px)] bg-brand-white p-6 gap-8 border border-border rounded-md shadow-custom-xs">
						{/* Left Panel: PhaseEditor or General Course Details */}
						<Stack
							className="w-full h-full overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent hover:scrollbar-thumb-gray-600 pr-4"
							spacing={"12px"}
						>
							<div className="flex flex-col gap-4 w-full">
								<div className="flex flex-col gap-1 w-full">
									<InputField
										containerClassName="w-full"
										error={!!errors.name?.message}
										errorMessage={errors.name?.message}
										handleChange={(e) => handleSaveFormState(e.target.value, "name")}
										label={ts("name") + "*"}
										name={"name"}
										placeholder={ts("name")}
										value={currentFormState?.name || ""}
									/>
								</div>
								<LanguageSelect
									currentLanguage={
										(currentFormState?.language?.toUpperCase() as UserLanguage) ?? "DE"
									}
									handleLanguageChange={(lang: UserLanguage) => {
										handleSaveFormState(lang, "language");
									}}
									languages={languages}
								/>
								<div className="flex flex-col md:flex-row gap-4 w-full">
									<AutoComplete
										data={
											TEMPLATES?.map((template) => ({
												id: template?.id ?? -1,
												title: template?.name ?? "",
											})) ?? []
										}
										handleSearch={() => {
											/* Implement if needed */
										}}
										handleSelect={handleSetTemplate}
										label={ts("template")}
										position="bottom"
										selectedItem={currentFormState?.json?.scenarioName ?? ""}
									/>
								</div>
								<div className="flex flex-col md:flex-row gap-4 w-full">
									<AutoComplete
										data={
											tags
												?.filter((tag) => !!tag?.id && !!tag?.name)
												?.map((tag) => ({ id: tag?.id ?? -1, title: tag?.name ?? "" })) ?? []
										}
										handleSearch={() => {
											/* Implement if needed */
										}}
										handleSelect={handleSetTags}
										label={ts("tags")}
										position="bottom"
										selectedItem={currentFormState?.tag?.name ?? ""}
									/>

									<InputField
										containerClassName="w-full"
										error={!!errors.author?.message}
										handleChange={(e) => {
											const newAuthor = e.target.value;

											handleSaveFormState(newAuthor, "author");
										}}
										label={ts("author")}
										name="author"
										placeholder={ts("author")}
										showError={!!errors.author?.message}
										value={currentFormState?.author || ""}
									/>
								</div>
								<div className="flex flex-col md:flex-row gap-4 w-full">
									<InputField
										className={`bg-white h-auto !overflow-hidden`}
										errorMessage={errors.description?.message}
										handleChange={(e) => handleSaveFormState(e.target.value, "description")}
										label={ts("description")}
										multiline={true}
										name={"description"}
										placeholder={ts("description")}
										showError={false}
										value={currentFormState?.description || ""}
									/>
									<InputField
										className={`bg-white h-auto !overflow-hidden`}
										errorMessage={errors.forWhom?.message}
										handleChange={(e) => handleSaveFormState(e.target.value, "forWhom")}
										label={ts("forWhom")}
										multiline={true}
										name={"forWhom"}
										placeholder={ts("forWhom")}
										showError={false}
										value={currentFormState?.forWhom || ""}
									/>
								</div>
								<div className="flex flex-col md:flex-row gap-4 w-full">
									<ImageUploader
										border
										aspectRatio={310 / 183}
										currentImgFileInfo={thumbnailImageFileInfo}
										description={t("basics.thumbnail-description")}
										fileAssignment={FileAssignment.Thumbnail}
										imagePrefix="course-thumbnail"
										label={t("basics.thumbnail")}
										maxFileSizeInBytes={2 * 1024 * 1024}
										setCurrentContentInfo={setCurrentShowingContentInfo}
										setCurrentImgFileInfo={setThumbnailImageFileInfo}
									/>
									<ImageUploader
										border
										aspectRatio={1330 / 459}
										currentImgFileInfo={largeImageFileInfo}
										description={t("basics.large-image-description")}
										fileAssignment={FileAssignment.LargeImage}
										imagePrefix="course-large-image"
										label={t("basics.large-image")}
										maxFileSizeInBytes={2 * 1024 * 1024}
										setCurrentContentInfo={setCurrentShowingContentInfo}
										setCurrentImgFileInfo={setLargeImageFileInfo}
									/>
								</div>
								<VideoUploader
									border
									currentVideoFileInfo={trailerVideoFileInfo}
									description={t("basics.trailer-video-description")}
									fileAssignment={FileAssignment.TrailerVideo}
									label={t("basics.trailer-video")}
									maxFileSizeInBytes={500 * 1024 * 1024}
									setCurrentContentInfo={setCurrentShowingContentInfo}
									setCurrentVideoFileInfo={setTrailerFileInfo}
									videoPrefix="course-trailer-video"
								/>
							</div>
						</Stack>
					</div>

					{/* Action Buttons */}
					<Stack
						alignItems="center"
						direction="row"
						flexWrap="wrap"
						justifyContent={"flex-end"}
						marginTop={2}
						pr={4}
						spacing={2}
					>
						<div className="w-fit">
							<Button
								className="!h-[44px]"
								color={ButtonColor.ACTION_SECONDARY}
								title={t("basics.cancel")}
								onClick={handleClose}
							/>
						</div>

						<div className="w-fit">
							<Button
								className="!h-[44px]"
								color={ButtonColor.ACTION}
								disabled={isLoading}
								isLoading={isLoading}
								title={t("basics.save")}
								onClick={handleSubmit(onSubmit)}
							/>
						</div>
					</Stack>
				</div>
				<ContentModal
					contentInfo={currentShowingContentInfo}
					setCurrentContentInfo={setCurrentShowingContentInfo}
				/>
			</>
		</DnDProvider>
	);
};
